// CUSTOM: bocket-contact-type-filter — BKT-027
// Estado compartido del filtro "Tipo de contacto" del menu lateral:
// el atributo de la cuenta y los conteos por valor, para contactos
// (BKT-027) y conversaciones (BKT-028). Las reglas puras viven en
// helper/bocket/contactTypeFilter.js y se reexportan aqui.
import { ref } from 'vue';
import { debounce } from '@chatwoot/utils';
import ContactTypeCountsAPI from 'dashboard/api/bocket/contactTypeCounts';
import {
  CONTACT_TYPE_TARGETS,
  isContactTypeFilterEnabled,
  isConversationsContactTypeEnabled,
} from 'dashboard/helper/bocket/contactTypeFilter';

export * from 'dashboard/helper/bocket/contactTypeFilter';

// Acciones del store que pueden cambiar los conteos, por destino.
// 'get' y 'filter' cubren volver a cargar la lista (p. ej. tras un borrado
// masivo). No se escucha 'updateContact' (eventos en vivo): llega con cada
// mensaje y dispararia consultas de mas.
const CONTACT_ACTIONS = [
  'contacts/update',
  'contacts/create',
  'contacts/delete',
  'contacts/deleteCustomAttributes',
  'contacts/merge',
];

const TARGET_CONFIG = {
  [CONTACT_TYPE_TARGETS.CONTACTS]: {
    isEnabled: isContactTypeFilterEnabled,
    actions: ['contacts/get', 'contacts/filter', ...CONTACT_ACTIONS],
    wait: 500,
    maxWait: undefined,
  },
  // 'conversationStats/get' llega con la actividad de conversaciones; el
  // debounce largo deja como maximo una consulta cada ~15 s por pestaña.
  [CONTACT_TYPE_TARGETS.CONVERSATIONS]: {
    isEnabled: isConversationsContactTypeEnabled,
    actions: [
      'fetchAllConversations',
      'conversationStats/get',
      ...CONTACT_ACTIONS,
    ],
    wait: 3000,
    maxWait: 15000,
  },
};

const createState = target => {
  const config = TARGET_CONFIG[target];
  const state = {
    attribute: ref(null),
    values: ref([]),
    noneCount: ref(0),
    loadedAccountId: null,
    storeSubscribed: false,
  };

  const reset = () => {
    state.attribute.value = null;
    state.values.value = [];
    state.noneCount.value = 0;
  };

  state.fetchCounts = async () => {
    if (!config.isEnabled()) return;
    try {
      const { data } = await ContactTypeCountsAPI.get(target);
      state.attribute.value = data.attribute;
      state.values.value = data.values || [];
      state.noneCount.value = data.none_count || 0;
    } catch {
      // Si falla, el menu simplemente no muestra la seccion.
      reset();
    }
  };
  state.refreshCounts = debounce(
    state.fetchCounts,
    config.wait,
    false,
    config.maxWait
  );
  state.reset = reset;
  state.config = config;
  return state;
};

const states = {};
const getState = target => {
  if (!states[target]) states[target] = createState(target);
  return states[target];
};

export function useContactTypeFilter(
  store,
  target = CONTACT_TYPE_TARGETS.CONTACTS
) {
  const state = getState(target);

  if (store && !state.storeSubscribed) {
    state.storeSubscribed = true;
    store.subscribeAction({
      after: action => {
        if (state.config.actions.includes(action.type)) state.refreshCounts();
      },
    });
  }

  const loadForAccount = accountId => {
    if (!accountId) return;
    if (state.loadedAccountId !== accountId) {
      state.reset();
      state.loadedAccountId = accountId;
    }
    state.fetchCounts();
  };

  return {
    attribute: state.attribute,
    values: state.values,
    noneCount: state.noneCount,
    loadForAccount,
    refreshCounts: state.refreshCounts,
  };
}
